/**
 * ScrapProtocolPage — Selejtezesi jegyzokonyvek
 * Epic 65: Selejtezés & Készletkorrekció
 */

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  useApproveScrapProtocol,
  useCreateScrapProtocol,
  useFinalizeScrapProtocol,
  useScrapProtocols,
} from '@/hooks/useScrap';
import { useTranslation } from '@kgc/i18n';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('hu-HU', {
    style: 'currency',
    currency: 'HUF',
    maximumFractionDigits: 0,
  }).format(amount);
}

const STATUS_LABELS: Record<string, string> = {
  DRAFT: 'inventory.scrapProtocolStatuses.draft',
  APPROVED: 'inventory.scrapProtocolStatuses.approved',
  FINALIZED: 'inventory.scrapProtocolStatuses.finalized',
};

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'bg-yellow-100 text-yellow-800',
  APPROVED: 'bg-blue-100 text-blue-800',
  FINALIZED: 'bg-green-100 text-green-800',
};

export function ScrapProtocolPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { result, isLoading, error } = useScrapProtocols();
  const createProtocol = useCreateScrapProtocol();
  const approveProtocol = useApproveScrapProtocol();
  const finalizeProtocol = useFinalizeScrapProtocol();

  const now = new Date();
  const [year, setYear] = useState(String(now.getFullYear()));
  const [month, setMonth] = useState(String(now.getMonth() + 1));

  const handleCreate = async () => {
    const y = parseInt(year, 10);
    const m = parseInt(month, 10);
    if (!y || !m || m < 1 || m > 12) return;
    await createProtocol.mutateAsync({ year: y, month: m });
  };

  if (isLoading) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <h1 className="text-2xl font-bold mb-6">{t('inventory.scrapProtocolsTitle')}</h1>
        <div className="text-center py-8 text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <h1 className="text-2xl font-bold mb-6">{t('inventory.scrapProtocolsTitle')}</h1>
        <Card>
          <CardContent className="p-8 text-center text-red-500">
            {t('page.errorPrefix', { message: error })}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">{t('inventory.scrapProtocolsTitle')}</h1>
        <Button variant="outline" onClick={() => navigate('/inventory/scrap')}>
          {t('inventory.scrapItems')}
        </Button>
      </div>

      {/* Create protocol */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>{t('inventory.newProtocol')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3 items-end">
            <div>
              <label className="text-sm text-muted-foreground">{t('inventory.year')}</label>
              <Input
                type="number"
                value={year}
                onChange={e => setYear(e.target.value)}
                className="w-24"
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground">{t('inventory.month')}</label>
              <Input
                type="number"
                value={month}
                onChange={e => setMonth(e.target.value)}
                className="w-20"
                min={1}
                max={12}
              />
            </div>
            <Button onClick={handleCreate} disabled={createProtocol.isPending}>
              {createProtocol.isPending ? t('inventory.creating') : t('inventory.create')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Protocol list */}
      <Card>
        <CardHeader>
          <CardTitle>{t('inventory.protocolsList', { count: result?.total ?? 0 })}</CardTitle>
        </CardHeader>
        <CardContent>
          {!result?.reports.length ? (
            <p className="text-muted-foreground text-center py-4">{t('inventory.noProtocols')}</p>
          ) : (
            <div className="divide-y">
              {result.reports.map(report => (
                <div key={report.id} className="py-3 flex justify-between items-center">
                  <div>
                    <span className="font-medium">
                      {report.year}-{String(report.month).padStart(2, '0')}
                    </span>
                    <span
                      className={`ml-3 text-xs px-2 py-1 rounded ${STATUS_COLORS[report.status] ?? ''}`}
                    >
                      {t(STATUS_LABELS[report.status] ?? '') || report.status}
                    </span>
                    <span className="ml-3 text-sm text-muted-foreground">
                      {report.totalItems} {t('inventory.items')}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-medium">{formatCurrency(report.totalValue)}</span>
                    {report.status === 'DRAFT' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => approveProtocol.mutate(report.id)}
                        disabled={approveProtocol.isPending}
                      >
                        {t('inventory.approve')}
                      </Button>
                    )}
                    {report.status === 'APPROVED' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => finalizeProtocol.mutate(report.id)}
                        disabled={finalizeProtocol.isPending}
                      >
                        {t('inventory.finalize')}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
